import { AppThunk } from "store/store";
import { setLoaderVisibility } from "store/common/initialization/initialization-actions";
import {
    setEnrollTenant,
    setOrchestratorInstanceId,
    setEnrollCache,
} from "./enroll-tenant-actions";
import { setErrorResponseMessage } from "store/common/initialization/initialization-actions";
import {
    startTenantEnroll,
    loadEnrollCache,
    loadEnrollWorkflow,
    approveEnrollStep as approveStep,
    terminateTenantEnroll,
} from "api/environments/tenants/tenant-enrollments-api";
import constants from "utils/constants";
import { getErrorMessage } from "api/defaults";
import { EnrollCache, StepApproveDataTypes } from "./enroll-tenant-types";

export const startEnroll =
    (environmentMoniker: string): AppThunk =>
    async (dispatch) => {
        dispatch(setLoaderVisibility(true));

        const [payload, statusCode] =
            await startTenantEnroll(environmentMoniker);

        if (payload && statusCode === constants.statusCode.OK) {
            dispatch(setOrchestratorInstanceId(payload));
        } else {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                )
            );
        }

        dispatch(setLoaderVisibility(false));
    };

export const fetchEnrollWorkflow =
    (environmentMoniker: string, orchestratorInstanceId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setLoaderVisibility(true));
        const [payload, statusCode] = await loadEnrollWorkflow(
            environmentMoniker,
            orchestratorInstanceId
        );

        if (payload && statusCode === constants.statusCode.OK) {
            dispatch(setEnrollTenant(payload));
        } else {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload || null), statusCode)
                )
            );
        }
        dispatch(setLoaderVisibility(false));
    };

export const fetchEnrollCache =
    (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ): AppThunk<Promise<[EnrollCache | null, number]>> =>
    async (dispatch) => {
        const [payload, statusCode] = await loadEnrollCache(
            environmentMoniker,
            orchestratorInstanceId
        );

        if (payload && statusCode === constants.statusCode.OK) {
            dispatch(setEnrollCache(payload));
        } else if (statusCode === constants.statusCode.NoContent) {
            dispatch(setEnrollTenant(null));
        } else if (statusCode === constants.statusCode.UnauthenticatedError) {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload || null), statusCode)
                )
            );
        }

        return [payload, statusCode];
    };

export const approveEnrollStep =
    (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        eventName: string,
        data: StepApproveDataTypes
    ): AppThunk =>
    async (dispatch) => {
        const [payload, statusCode] = await approveStep(
            environmentMoniker,
            orchestratorInstanceId,
            eventName,
            data
        );

        if (payload && statusCode !== constants.statusCode.OK) {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                )
            );
        }
    };

export const terminateEnroll =
    (environmentMoniker: string, orchestratorInstanceId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setLoaderVisibility(true));
        const [payload, statusCode] = await terminateTenantEnroll(
            environmentMoniker,
            orchestratorInstanceId
        );
        if (payload && statusCode !== constants.statusCode.OK) {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                )
            );
        }
        dispatch(setLoaderVisibility(false));
    };

export const retryEnrollStep =
    (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        data: StepApproveDataTypes
    ): AppThunk =>
    async (dispatch) => {
        const [payload, statusCode] = await approveStep(
            environmentMoniker,
            orchestratorInstanceId,
            constants.retryFailedStepEventName,
            data
        );

        if (payload && statusCode !== constants.statusCode.OK) {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                )
            );
        }
    };

export const continueEnrollStep =
    (
        environmentMoniker: string,
        orchestratorInstanceId: string,
        data: StepApproveDataTypes
    ): AppThunk =>
    async (dispatch) => {
        const [payload, statusCode] = await approveStep(
            environmentMoniker,
            orchestratorInstanceId,
            constants.completeStepEventName,
            data
        );

        if (payload && statusCode !== constants.statusCode.OK) {
            dispatch(
                setErrorResponseMessage(
                    getErrorMessage(String(payload), statusCode)
                )
            );
        }
    };
